'use client';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from '~/auth';
import { CreateAccountProvider, CreateAccountState, useCreateAccount } from '~/onboarding';
import { MobilePageHeader, ProcessDetails, WizardLayout } from '~/ui';

import { CompletionStep } from './CompletionStep';
import { EmailStep } from './EmailStep';
import { PasswordStep } from './PasswordStep';
import { PhoneNumberStep } from './PhoneNumberStep';
import { RegisterIntroductionStep } from './RegisterIntroductionStep';

const STEPS = {
  completion: CompletionStep,
  email: EmailStep,
  password: PasswordStep,
  phoneNumber: PhoneNumberStep,
  registerIntroduction: RegisterIntroductionStep,
};

const Wizard = ({ onCompletion }: { onCompletion: () => void }) => {
  const { t } = useTranslation('register');
  const { getCurrentStep, goToPreviousStep } = useCreateAccount();
  const step = getCurrentStep();

  const { isAuthenticated } = useAuthUser();

  useEffect(() => {
    if (isAuthenticated() && step === 'registerIntroduction') {
      onCompletion();
    }
  }, [isAuthenticated, onCompletion, step]);

  const CurrentStep = STEPS[step as keyof typeof STEPS] || RegisterIntroductionStep;

  const showBackButton = step !== 'registerIntroduction' && step !== 'completion';

  return (
    <WizardLayout
      leftChild={
        <ProcessDetails
          title={t('title')}
          subtitle={t('subtitle')}
          showBackButton={showBackButton}
          onBackClick={goToPreviousStep}
          iconSource="/images/onboarding-icons/account-icon.svg"
        />
      }
      rightChild={<CurrentStep />}
      header={<MobilePageHeader title={t('title')} onBackClick={goToPreviousStep} showBackButton={showBackButton} />}
    />
  );
};

interface CreateAccountWizardProps {
  onCompletion: () => void;
  onUpdate: (data: CreateAccountState['accountData']) => void;
}

const CreateAccountWizard = ({ onCompletion, onUpdate }: CreateAccountWizardProps) => (
  <CreateAccountProvider onCompletion={onCompletion} onUpdate={onUpdate}>
    <Wizard onCompletion={onCompletion} />
  </CreateAccountProvider>
);

export default CreateAccountWizard;
